/**
 * @zendir/ui - Checkbox Component
 *
 * Checkbox and Radio button following Astro UX Design System with Zendir purple accents.
 *
 * Astro UX Compliance:
 * - Clear visual states
 * - Accessible keyboard support
 * - Indeterminate state for checkbox
 *
 * Zendir Enhancements:
 * - Purple accent when checked
 * - Glow effect on focus
 * - Smooth spring animation
 *
 * @example
 * ```tsx
 * <Checkbox checked={agreed} onChange={setAgreed} label="I agree to terms" />
 * <Checkbox type="radio" name="priority" value="high" label="High Priority" />
 * ```
 */

import React, { memo, forwardRef, useId, useState } from "react";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";

export interface CheckboxProps {
  /** Checkbox or radio */
  type?: "checkbox" | "radio";
  /** Checked state */
  checked?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Label text */
  label?: string;
  /** Value (for radio groups) */
  value?: string;
  /** Name (for radio groups) */
  name?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Indeterminate state (checkbox only) */
  indeterminate?: boolean;
  /** Size variant */
  size?: "small" | "medium";
  /** Custom className */
  className?: string;
}

export const Checkbox = memo(
  forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
    {
      type = "checkbox",
      checked = false,
      onChange,
      label,
      value,
      name,
      disabled = false,
      indeterminate = false,
      size = "medium",
      className = "",
    },
    ref,
  ): React.ReactElement {
    const { tokens, theme } = useTheme();
    const isTransparentTheme =
      theme === "transparent" ||
      theme === "transparent-bold" ||
      theme === "transparent-minimal";
    const transparentDefault = tokens.colors.interactive.transparentDefault;
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const id = useId();

    // WCAG 2.5.8: minimum 24×24 CSS px target size
    const sizeConfig = {
      small: { boxSize: 18, iconSize: 12, targetSize: 24 },
      medium: { boxSize: 20, iconSize: 14, targetSize: 24 },
    };
    const config = sizeConfig[size];

    const isRadio = type === "radio";
    const borderRadius = isRadio ? "50%" : tokens.borderRadius.md;

    const boxStyle: React.CSSProperties = {
      width: config.boxSize,
      height: config.boxSize,
      // Unchecked state uses border.default, not border.muted: the muted
      // token is a hairline that themes may set equal to an elevated surface
      // colour (nebula does), which makes the box invisible inside panels.
      // A control outline is an affordance and needs the stronger token.
      border: `${tokens.borders.width.thick} solid ${checked ? tokens.colors.accent.primary : hovered && !disabled ? `${tokens.colors.accent.primary}60` : tokens.colors.border.default}`,
      borderRadius,
      backgroundColor: checked
        ? tokens.colors.accent.primary
        : isTransparentTheme && transparentDefault
          ? transparentDefault
          : "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: `${tokens.animation.normal}, opacity ${tokens.animation.duration.normal}ms ${tokens.animation.easing.default}`,
      flexShrink: 0,
      boxShadow: checked
        ? `0 0 12px ${tokens.colors.accent.primary}40`
        : focused
          ? tokens.borders.focusRing.subtle
          : "none",
      transform: checked
        ? "scale(1)"
        : hovered && !disabled
          ? "scale(1.05)"
          : "scale(1)",
    };

    // Check mark for checkbox
    const checkIcon = (
      <svg
        width={config.iconSize}
        height={config.iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {indeterminate ? (
          <line x1="5" y1="12" x2="19" y2="12" />
        ) : (
          <polyline points="20 6 9 17 4 12" />
        )}
      </svg>
    );

    // Dot for radio
    const radioIcon = (
      <div
        style={{
          width: config.iconSize - 4,
          height: config.iconSize - 4,
          borderRadius: "50%",
          backgroundColor: "#ffffff",
        }}
      />
    );

    return (
      <label
        htmlFor={id}
        className={classNames("zendir-checkbox", className)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {/* Touch target wrapper — ensures WCAG 2.5.8 24px minimum */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: config.targetSize,
            minHeight: config.targetSize,
          }}
        >
          <div style={boxStyle}>
            {checked && (isRadio ? radioIcon : checkIcon)}
          </div>
        </div>

        <input
          ref={ref}
          type={type}
          id={id}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            position: "absolute",
            opacity: 0,
            width: 0,
            height: 0,
          }}
        />

        {label && (
          <span
            style={{
              fontSize: "0.875rem",
              color: tokens.colors.text.primary,
              transition: tokens.animation.fast,
            }}
          >
            {label}
          </span>
        )}
      </label>
    );
  }),
);

export default Checkbox;
